import * as React from 'react';
import * as Yup from 'yup';

import { Button, FormGroup, FormText, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Formik, FormikActions, FormikProps } from 'formik';
import { mergeFields, processApiError, toFormGroupStyle } from 'common/utils/form';

import { FormErrorFeedback } from 'common/components/form/FormErrorFeedback';
import { TAG_TYPES } from './Tags';
import { Tag } from 'common/api/models/Tag';
import { TagApi } from 'common/api/resources/TagApi';
import { toSlug } from 'common/utils/utils';
import { Analytics } from 'common/utils/Analytics';
import { FormInput } from 'common/components/form/FormInput';
import { MediaUpload } from 'common/components/form/MediaUpload';
import { Feature } from 'common/features/Feature';
import { AppState } from 'common/models/AppState';
import { canViewBadges } from 'common/features/canViewBadges';

interface TagsDialogProps {
  tag?: Tag;
  appState: AppState;
  onSave: (saved: string) => void;
  onCancel: () => void;
}

class TagsFormValues {
  name: string = '';
  shortName: string = '';
  imagePath: string = '';
  description: string = '';
  type: string = '';
}

export class TagsDialog extends React.Component<TagsDialogProps> {
  tagTypes = canViewBadges(Feature.badges, this.props.appState.config, this.props.appState.user)
    ? [...TAG_TYPES, { label: 'Badge', value: 'BADGE' }]
    : TAG_TYPES;

  initialValues() {
    return mergeFields(new TagsFormValues(), this.props.tag);
  }

  handleSubmit = (values: TagsFormValues, actions: FormikActions<TagsFormValues>) => {
    const TagData = {
      ...values,
      imagePath: values.imagePath === '' ? '' : values.imagePath,
      type: Tag.Type[values.type],
    };
    const call =
      this.props.tag && this.props.tag.id ? TagApi.updateTag(this.props.tag.id, TagData) : TagApi.createTag(TagData);
    const analyticsEvent = this.props.tag && this.props.tag.id ? Analytics.tag.update : Analytics.tag.create;
    call.then(
      res => {
        this.props.onSave(this.props.tag && this.props.tag.id ? this.props.tag.id : (res as any).id);
        analyticsEvent();
        actions.setSubmitting(false);
        actions.setStatus(undefined);
      },
      err => {
        processApiError<TagsDialogProps, TagsFormValues>(err, actions);
      }
    );
  };

  render() {
    const title = this.props.tag && this.props.tag.id ? 'Edit Tag' : 'Add Tag';

    return (
      <Modal size="md" isOpen={!!this.props.tag} toggle={this.props.onCancel} keyboard={true}>
        <Formik
          initialValues={this.initialValues()}
          validationSchema={Yup.object().shape({
            name: Yup.string().required('Required'),
            type: Yup.string().required('Required'),
            description: Yup.string()
              .min(10, 'Please describe this tag so people in 6 months will know what it is.')
              .required('Required'),
          })}
          onSubmit={this.handleSubmit}
          render={(form: FormikProps<TagsFormValues>) => (
            <form onSubmit={form.handleSubmit} noValidate={true}>
              <ModalHeader toggle={this.props.onCancel}>{title}</ModalHeader>
              <ModalBody>
                {this.props.tag &&
                  this.props.tag.id && (
                    <div className="mb-3">
                      <b>PLEASE READ:</b> Tags describe users, orgs, and members, and photos. Some tags are editable by
                      members while others are not. <b>Tags are NOT deletable, only editable!</b>. Editing tags will
                      update that tag everywhere. Be mindful when changing the name or meaning of tags.
                    </div>
                  )}
                <FormGroup color={toFormGroupStyle('name', form)}>
                  <Label for="name">Full Name</Label>
                  <Input
                    id="name"
                    value={form.values.name}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                    spellCheck={false}
                    autoComplete="off"
                  />
                  <FormErrorFeedback field="name" form={form} />
                </FormGroup>
                <FormInput id="shortName" name="shortName" label="Short Name" spellCheck={false} autoComplete="off" />
                <FormInput id="type" tag="select" name="type" label="Type">
                  {this.tagTypes.map(
                    opt =>
                      opt.value !== '' && (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      )
                  )}
                </FormInput>
                <FormGroup color={toFormGroupStyle('description', form)}>
                  <Label for="description">Description</Label>
                  <Input
                    type="textarea"
                    id="description"
                    value={form.values.description}
                    onChange={form.handleChange}
                    onBlur={form.handleBlur}
                    autoComplete="off"
                  />
                  <FormErrorFeedback field="description" form={form} />
                  <FormText color="muted">Please be concise and specific.</FormText>
                </FormGroup>
                <FormGroup color={toFormGroupStyle('imagePath', form)}>
                  <Label for="imagePath">Image</Label>
                  <MediaUpload
                    id="imagePath"
                    size="xl"
                    value={form.values.imagePath}
                    slug={toSlug(form.values.name)}
                    onChange={form.setFieldValue}
                  />
                  <FormErrorFeedback field="imagePath" form={form} />
                  <FormText color="muted">
                    This is the image used on the homepage and feature landing pages like Jobs, Photos, and Members
                  </FormText>
                </FormGroup>
                {form.status && <div className="alert alert-danger">{form.status}</div>}
              </ModalBody>
              <ModalFooter>
                <div>
                  <Button color="secondary" onClick={this.props.onCancel}>
                    Cancel
                  </Button>{' '}
                  <Button color="primary" type="submit">
                    Save
                  </Button>
                </div>
              </ModalFooter>
            </form>
          )}
        />
      </Modal>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Tags/TagDialog.tsx